import * as R from 'ramda'
import * as Result from './Result'

export function validateInputField<Input, Validation, Data>(
    validator: (value: Input) => Result.Result<Validation, Data>,
    submissionAttempted: boolean,
    value: Input,
): Validation | null {
    return submissionAttempted ?
        Result.errorToNullable(validator(value)) :
        null
}

export const mandatoryNullableValidator =
    () =>
        <T>(value: T | null): Result.Result<'Mandatory', T> =>
            value !== null ?
                {type: 'Ok', value} :
                {type: 'Err', error: 'Mandatory'}

export const mandatoryStringValidator =
    () =>
        (value: string): Result.Result<'Mandatory', string> =>
            value !== '' ?
                {type: 'Ok', value} :
                {type: 'Err', error: 'Mandatory'}

export const emptyStringToNullDecoder =
    <Validation, Value>(validator: (value: string) => Result.Result<Validation, Value>) =>
        (value: string): Result.Result<Validation, Value | null> =>
            value !== ''
                ? validator(value)
                : {type: 'Ok', value: null}

export const emailRegex =
// tslint:disable-next-line
    /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

export const emailValidator =
    () =>
        (value: string): Result.Result<'InvalidEmail', string> =>
            emailRegex.test(value) ?
                {type: 'Ok', value} :
                {type: 'Err', error: 'InvalidEmail'}

export const stringToIntValidator =
    () =>
        (value: string): Result.Result<'InvalidInt', number> =>
            /^-?\d+$/.test(value) ?
                {type: 'Ok', value: parseInt(value, 10)} :
                {type: 'Err', error: 'InvalidInt'}

export const stringToFixedPointNumberValidator =
    (decimals: number) =>
        (value: string): Result.Result<'InvalidFixedPointNumber', number> =>
            new RegExp(`^\\d*(\\.\\d{3})*(,\\d{0,${decimals}})?$`).test(value) ?
                {type: 'Ok', value: parseFloat(value.replace('.', '').replace(',', '.'))} :
                {type: 'Err', error: 'InvalidFixedPointNumber'}

export const arrayHasUniqueNumberKeyValidator =
    <T>(arrayList: T[], valueCompare: (val: T) => number) =>
        (value: T): Result.Result<'NotUniqueInArray', number> => {
            if (R.find((arrayEntry: T) => {
                    return valueCompare(arrayEntry) === valueCompare(value)
                }, arrayList)) {
                return {type: 'Ok', value: valueCompare(value)}
            }
            return {type: 'Err', error: 'NotUniqueInArray'}
        }
export const arrayHasUniqueStringKeyValidator =
    <T>(arrayList: T[], valueCompare: (val: T) => string) =>
        (value: string): Result.Result<'NotUniqueInArray', string> => {
            let result = R.filter((arrayEntry: T) => {
                return valueCompare(arrayEntry) === value
            }, arrayList)
            if (result.length <= 1) {
                return {type: 'Ok', value: value}
            }
            return {type: 'Err', error: 'NotUniqueInArray'}
        }

export const nonemptyArrayValidator =
    () =>
        <T>(value: T[]): Result.Result<'EmptyArray', T[]> =>
            value.length !== 0 ?
                {type: 'Ok', value} :
                {type: 'Err', error: 'EmptyArray'}

export const minimumValidator =
    (reference: number) =>
        (value: number): Result.Result<'TooSmall', number> =>
            value >= reference ?
                {type: 'Ok', value} :
                {type: 'Err', error: 'TooSmall'}

export const maximumValidator =
    (reference: number) =>
        (value: number): Result.Result<'TooBig', number> =>
            value <= reference ?
                {type: 'Ok', value} :
                {type: 'Err', error: 'TooBig'}

export const equalsValidator =
    <T>(reference: T) =>
        (value: T): Result.Result<'NotEqual', T> =>
            value === reference ?
                {type: 'Ok', value} :
                {type: 'Err', error: 'NotEqual'}

export const lookupValidator =
    <Key, ReferencedValue>(
        fn: (referencedValue: ReferencedValue) => Key,
        list: ReferencedValue[]
    ) =>
        (key: Key): Result.Result<'LookupFailed', ReferencedValue> => {
            const searchResult =
                R.find(
                    value => fn(value) === key,
                    list
                )
            return typeof searchResult !== 'undefined'
                ? {type: 'Ok', value: searchResult}
                : {type: 'Err', error: 'LookupFailed'}
        }

export function validateRows<Data, Row>(
    validate: (row: Row) => ValidationOutcome<Data, Row>,
    rows: Row[]
): ValidationOutcomeList<Data, Row> {
    return R.reduce<Row, ValidationOutcomeList<Data, Row>>(
        (previous, row) => {
            const validationOutcome = validate(row)
            return {
                data:
                    previous.data !== null && validationOutcome.data !== null ?
                        R.append(
                            validationOutcome.data,
                            previous.data,
                        ) :
                        null,
                rows: R.append(validationOutcome.state, previous.rows),
            }
        },
        {data: [], rows: []},
        rows
    )
}

export interface ValidationOutcome<Data, State> {
    readonly state: State
    readonly data: Data | null
}

export interface ValidationOutcomeList<Data, Row> {
    readonly data: Data[] | null
    readonly rows: Row[]
}

export type Error =
    | 'Mandatory'
    | 'InvalidEmail'
    | 'InvalidInt'
    | 'InvalidFixedPointNumber'
    | 'EmptyArray'
    | 'TooSmall'
    | 'TooBig'
    | 'NotEqual'
    | 'LookupFailed'
    | 'NotUniqueInArray'

export function errorMessage(error: Error | null): string {
    switch (error) {
        case null:
            return ''
        case 'Mandatory':
            return 'Pflichtfeld'
        case 'InvalidEmail':
            return 'Ungültige E-Mail-Adresse'
        case 'InvalidInt':
            return 'Ungültige Ganzzahl'
        case 'InvalidFixedPointNumber':
            return 'Ungültige Kommazahl'
        case 'EmptyArray':
            return 'Leere Auswahl'
        case 'TooSmall':
            return 'Wert zu klein'
        case 'TooBig':
            return 'Wert zu groß'
        case 'NotEqual':
            return 'Keine Übereinstimmung mit vorherigem Wert'
        case 'LookupFailed':
            return 'Auswahl ist nicht mehr verfügbar'
        case 'NotUniqueInArray':
            return 'Nicht einzigartig'
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Data/FormValidation.ts